import {Component, Inject, OnDestroy, OnInit, Renderer2} from '@angular/core';
import {DOCUMENT} from '@angular/common';
import {Category, NewsPage, User} from '../../shared/models';
import {Subject} from 'rxjs';
import {ActivatedRoute, Router} from '@angular/router';
import {CmsService} from '../../shared/services/cms.service';
import {takeUntil} from 'rxjs/operators';
import {AuthService} from '../../shared/modules/auth/auth.service';
import {SeoUtil} from '../../shared/utils';
import {environment} from '../../../environments/environment';

@Component({
  selector: 'app-news-pages',
  templateUrl: './news-pages.component.html'
})
export class NewsPagesComponent implements OnInit, OnDestroy {
  readonly pageSize: number = 15;

  user: User;

  categoryUrl: string;
  category: Category;
  newsPages: Array<NewsPage> = [];
  mostReadPages: Array<NewsPage> = [];
  selectedPage: number = 1;
  totalPages: number = 0;

  banner: any = null;

  loading: boolean;
  loadingMore: boolean;
  loadingMostRead: boolean;

  private destroy$ = new Subject();

  constructor(@Inject(DOCUMENT) private document: Document,
              private renderer: Renderer2,
              private route: ActivatedRoute,
              private router: Router,
              private cmsService: CmsService,
              private authService: AuthService,
              private seoUtil: SeoUtil) {
  }

  ngOnInit(): void {
    this.renderer.addClass(this.document.body, 'p-newsRubric');
    this.fetchCategory(this.route.snapshot.params['category']);
    this.fetchMostReadPages();
    this.handleRouteChange();
    this.handleUserChange();
  }

  ngOnDestroy() {
    this.renderer.removeClass(this.document.body, 'p-newsRubric');
    this.seoUtil.setDefaultMetaTags();
    this.seoUtil.setDefaultTitle();
    this.destroy$.next();
    this.destroy$.complete();
  }

  fetchCategory(category: string): void {
    this.categoryUrl = category;
    this.category = null;
    this.cmsService.getCategory(this.categoryUrl).subscribe(category => {
      this.category = category;
      this.seoUtil.updateTitle(category.name);
      this.seoUtil.updateMetaTags([
        {property: 'og:title', content: category.name},
        {property: 'og:description', content: category.description},
        {property: 'og:url', content: environment.sites.root + category.urlSlug}
      ]);
    }, () => {
      this.router.navigate(['/not-found'], {skipLocationChange: true}).finally();
    });
    this.fetchNews();
  }

  fetchMostReadPages(): void {
    this.loadingMostRead = true;
    this.cmsService.getMostReadPages().subscribe(pages => {
      this.mostReadPages = pages;
      this.loadingMostRead = false;
    }, () => {
      this.loadingMostRead = false;
    });
  }

  loadPage(page: number): void {
    if (page < 0) {
      return;
    }
    this.selectedPage = page;
    this.fetchNews();
  }

  private fetchNews(): void {
    if (this.selectedPage === 1) {
      this.loading = true;
    } else {
      this.loadingMore = true;
    }
    this.cmsService.getPages(this.categoryUrl, this.selectedPage, this.pageSize)
      .subscribe(resp => {
        this.totalPages = resp.total_pages;
        if (this.selectedPage === 1) {
          this.newsPages = resp.data;
          this.loading = false
        } else {
          this.newsPages.push(...resp.data);
          this.loadingMore = false;
        }
      });
  }

  private handleRouteChange(): void {
    this.route.params
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        let category = params['category'];
        if (category) {
          this.fetchCategory(category);
        }
      });
  }

  private handleUserChange() {
    if (this.authService.isLoggedIn()) {
      this.authService.getAuthUser().subscribe(user => {
        this.user = user;
      });
    } else {
      this.user = null;
    }
    this.authService.userChanged()
      .pipe(takeUntil(this.destroy$))
      .subscribe(user => {
        this.user = user;
      });
  }
}
